/**
 * Competitor Intelligence Panel
 * ==============================
 * Tracks competitor pricing, promotions, reviews, and market positioning.
 * Uses demo data - would connect to web scraping/API services in production.
 */

import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  Eye, TrendingUp, TrendingDown, Star, DollarSign, Target,
  AlertTriangle, ChevronRight, ExternalLink, Minus, Award
} from 'lucide-react';

const threatColors = {
  high: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', badge: 'bg-red-500' },
  medium: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-500' },
  low: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-500' },
};

const CompetitorRow = ({ competitor, yourBusiness }) => {
  const style = threatColors[competitor.threatLevel];
  const priceDiff = competitor.avgPrice - competitor.yourPrice;
  const priceDiffFormatted = Math.abs(priceDiff).toFixed(2);
  const ratingDiff = competitor.yourRating - competitor.rating;
  
  return (
    <div className={`p-3 rounded-xl ${style.bg} border ${style.border} group hover:shadow-md transition-all`}>
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-white/60 flex items-center justify-center">
            <Eye className={`w-4 h-4 ${style.text}`} />
          </div>
          <div>
            <h4 className="font-semibold text-gray-900 text-sm">{competitor.name}</h4>
            <span className="text-xs text-gray-500">{competitor.type} Competitor</span>
          </div>
        </div>
        <Badge className={`text-[10px] ${style.badge} text-white`}>
          {competitor.threatLevel} threat
        </Badge>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-2">
        <div className="text-center p-1.5 rounded-lg bg-white/50">
          <div className="flex items-center justify-center gap-1">
            <DollarSign className="w-3 h-3 text-gray-400" />
            <span className="text-sm font-bold">${competitor.avgPrice}</span>
          </div>
          <p className="text-[10px] text-gray-500">Avg Price</p>
          <span className={`text-[10px] font-medium ${priceDiff > 0 ? 'text-emerald-600' : priceDiff < 0 ? 'text-red-600' : 'text-gray-500'}`}>
            {priceDiff > 0 ? `You're $${priceDiffFormatted} cheaper` : priceDiff < 0 ? `You're $${priceDiffFormatted} more` : 'Same price'}
          </span>
        </div>
        <div className="text-center p-1.5 rounded-lg bg-white/50">
          <div className="flex items-center justify-center gap-1">
            <Star className="w-3 h-3 text-amber-400 fill-amber-400" />
            <span className="text-sm font-bold">{competitor.rating}</span>
          </div>
          <p className="text-[10px] text-gray-500">Rating</p>
          <span className={`text-[10px] font-medium ${ratingDiff > 0 ? 'text-emerald-600' : ratingDiff < 0 ? 'text-red-600' : 'text-gray-500'}`}>
            {ratingDiff > 0 ? `You're +${ratingDiff.toFixed(1)} higher` : ratingDiff < 0 ? `You're ${ratingDiff.toFixed(1)} lower` : 'Same rating'}
          </span>
        </div>
        <div className="text-center p-1.5 rounded-lg bg-white/50">
          <div className="flex items-center justify-center gap-1">
            <Target className="w-3 h-3 text-blue-500" />
            <span className="text-sm font-bold">{competitor.marketShare}%</span>
          </div>
          <p className="text-[10px] text-gray-500">Market Share</p>
          <span className="text-[10px] font-medium text-gray-500 flex items-center justify-center gap-0.5">
            {competitor.trend === 'up' ? <TrendingUp className="w-3 h-3 text-red-500" /> : 
             competitor.trend === 'down' ? <TrendingDown className="w-3 h-3 text-emerald-500" /> :
             <Minus className="w-3 h-3 text-gray-400" />}
            {competitor.trend}
          </span>
        </div>
      </div>
      
      {competitor.recentPromo && (
        <div className="flex items-center gap-2 p-2 rounded-lg bg-white/50 border border-white/50">
          <AlertTriangle className="w-3.5 h-3.5 text-amber-500 flex-shrink-0" />
          <span className="text-xs text-gray-700 flex-1">
            <strong>Active Promo:</strong> {competitor.recentPromo}
          </span>
        </div>
      )}
    </div>
  );
};

const CompetitorIntelligencePanel = ({ 
  industry = 'hospitality',
  data = null,
  className = '',
  compact = false,
  onViewDetails
}) => {
  const [showAll, setShowAll] = useState(false);
  // Real data only — never render placeholder/demo competitors.
  const competitors = Array.isArray(data) ? data : [];
  const hasData = competitors.length > 0;

  if (!hasData) {
    return (
      <div className={`relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06)] ${compact ? 'p-5' : 'p-6'} ${className}`} data-testid="competitor-intelligence-empty">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shadow-lg shadow-blue-500/25">
            <Eye className="w-5 h-5 text-white" />
          </div>
          <h3 className="font-semibold text-gray-900 tracking-tight">Competitor Intel</h3>
        </div>
        <div className="flex flex-col items-center justify-center text-center py-8 px-4 rounded-2xl bg-slate-50/60 border border-dashed border-slate-200">
          <Target className="w-8 h-8 text-slate-300 mb-3" />
          <p className="text-sm font-medium text-slate-700">No competitor data yet</p>
          <p className="text-xs text-slate-500 mt-1 max-w-xs">We'll track real competitor pricing, ratings and market share once we've scanned your market and connected your sources — no placeholder numbers.</p>
        </div>
      </div>
    );
  }

  const displayedCompetitors = compact ? competitors.slice(0, 2) : (showAll ? competitors : competitors.slice(0, 3));
  
  // Calculate your market position
  const yourMarketShare = 100 - competitors.reduce((sum, c) => sum + c.marketShare, 0);
  const avgCompetitorRating = competitors.length ? competitors.reduce((sum, c) => sum + c.rating, 0) / competitors.length : 0;
  const yourRating = competitors[0]?.yourRating || 4.8;
  
  if (compact) {
    return (
      <div className={`relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06),inset_0_0_0_1px_rgba(255,255,255,0.5)] p-5 transition-all duration-300 hover:shadow-[0_12px_40px_rgb(0,0,0,0.1)] hover:-translate-y-0.5 ${className}`} data-testid="competitor-intelligence-compact">
        <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" />
        <div className="relative">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shadow-lg shadow-blue-500/20">
                <Eye className="w-4 h-4 text-white" />
              </div>
              <h4 className="font-semibold text-gray-900 tracking-tight">Competitors</h4>
            </div>
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100 whitespace-nowrap">{competitors.length} tracked</span>
          </div>
          <div className="space-y-2">
            {displayedCompetitors.map(comp => (
              <div key={comp.id} className="flex items-center justify-between p-3 rounded-2xl bg-gradient-to-r from-gray-50/80 to-white/60 border border-gray-100/50">
                <span className="text-sm font-medium text-gray-900 truncate">{comp.name}</span>
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold ${threatColors[comp.threatLevel].badge} text-white whitespace-nowrap`}>
                  {comp.threatLevel}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden rounded-3xl bg-white/70 backdrop-blur-2xl border border-white/30 shadow-[0_8px_32px_rgb(0,0,0,0.06),inset_0_0_0_1px_rgba(255,255,255,0.5)] p-6 transition-all duration-300 hover:shadow-[0_12px_40px_rgb(0,0,0,0.1)] ${className}`} data-testid="competitor-intelligence-panel">
      {/* Background gradient orb */}
      <div className="absolute -top-16 -right-16 w-48 h-48 bg-gradient-to-br from-blue-100/40 to-indigo-200/30 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" />
      
      <div className="relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-400 to-indigo-500 flex items-center justify-center shadow-lg shadow-blue-500/25">
              <Eye className="w-5 h-5 text-white" />
            </div>
            <div className="flex items-center gap-2.5">
              <h3 className="font-semibold text-gray-900 tracking-tight">Competitor Intel</h3>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-100 whitespace-nowrap">{competitors.length} tracked</span>
            </div>
          </div>
          <button 
            onClick={onViewDetails}
            className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-full transition-colors whitespace-nowrap"
          >
            Full Report <ExternalLink className="w-3 h-3 ml-1" />
          </button>
        </div>

        {/* Your Position Summary */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
        <div className="text-center p-3 rounded-xl bg-gradient-to-br from-emerald-50 to-blue-50 border border-emerald-200">
          <div className="flex items-center justify-center gap-1 mb-1">
            <Award className="w-4 h-4 text-emerald-600" />
            <span className="text-lg font-bold text-emerald-700">{yourMarketShare}%</span>
          </div>
          <p className="text-[10px] text-emerald-600 font-medium">Your Market Share</p>
        </div>
        <div className="text-center p-3 rounded-xl bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-200">
          <div className="flex items-center justify-center gap-1 mb-1">
            <Star className="w-4 h-4 text-amber-500 fill-amber-500" />
            <span className="text-lg font-bold text-amber-700">{yourRating}</span>
          </div>
          <p className="text-[10px] text-amber-600 font-medium">Your Rating</p>
          <span className="text-[10px] text-emerald-600">+{(yourRating - avgCompetitorRating).toFixed(1)} vs avg</span>
        </div>
        <div className="text-center p-3 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
          <div className="flex items-center justify-center gap-1 mb-1">
            <Target className="w-4 h-4 text-blue-600" />
            <span className="text-lg font-bold text-blue-700">#1</span>
          </div>
          <p className="text-[10px] text-blue-600 font-medium">Market Position</p>
        </div>
      </div>

      {/* Competitor List */}
      <div className="space-y-3">
        {displayedCompetitors.map(competitor => (
          <CompetitorRow key={competitor.id} competitor={competitor} />
        ))}
      </div>

      {competitors.length > 3 && !showAll && (
        <Button 
          variant="ghost" 
          size="sm" 
          className="w-full mt-3 text-xs"
          onClick={() => setShowAll(true)}
        >
          View All {competitors.length} Competitors <ChevronRight className="w-3 h-3 ml-1" />
        </Button>
      )}
      </div>
    </div>
  );
};

export default CompetitorIntelligencePanel;
